import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FolderSearch, Library, ScrollText, CheckCircle2, FileCheck, Clock, FileEdit, AlertTriangle } from "lucide-react";

const WORKFLOW = ["Research", "Sources", "Claims", "Verification", "Content", "Editorial Review", "Publication"];

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#121212] border border-zinc-800 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <span className="label-caps text-zinc-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 font-mono-data font-bold text-3xl">{value ?? "—"}</div>
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);

  const load = () => api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runReconcile = async () => {
    try {
      const { data } = await api.post("/reconciliation/run");
      toast.success(`Reconciliation complete: ${data.resolved} resolved, ${data.failed} failed`);
      load();
    } catch (err) {
      toast.error("Reconciliation failed (super admin only)");
    }
  };

  return (
    <div className="p-6">
      {s?.reconciliation_needed && (
        <div data-testid="reconciliation-warning" className="mb-4 flex items-center gap-3 border border-amber-600/50 bg-amber-500/10 text-amber-200 rounded-sm px-4 py-3">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="flex-1 text-sm">
            <strong>{s.incomplete_operations}</strong> incomplete publication operation(s) detected. Data may be in an intermediate state.
          </div>
          <button data-testid="run-reconciliation-btn" onClick={runReconcile} className="text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-black rounded-sm px-3 py-1.5">
            Run Reconciliation
          </button>
        </div>
      )}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-tactical font-black text-2xl tracking-tight">Command Overview</h1>
          <p className="text-sm text-zinc-500 mt-1">Research and content production pipeline.</p>
        </div>
      </div>

      {/* Workflow strip */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="workflow-strip">
        {WORKFLOW.map((w, i) => (
          <div key={w} className="flex items-center gap-2">
            <span className="text-xs font-mono-data text-zinc-400 bg-zinc-900 border border-zinc-800 rounded-sm px-2.5 py-1">
              {String(i + 1).padStart(2, "0")} {w}
            </span>
            {i < WORKFLOW.length - 1 && <span className="text-zinc-700">→</span>}
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpis">
        <Kpi icon={FolderSearch} label="Projects" value={s?.projects} accent="text-sky-400" />
        <Kpi icon={Library} label="Sources" value={s?.sources} accent="text-violet-400" />
        <Kpi icon={ScrollText} label="Claims" value={s?.claims} accent="text-amber-400" />
        <Kpi icon={CheckCircle2} label="Verified Claims" value={s?.verified_claims} accent="text-emerald-400" />
        <Kpi icon={FileEdit} label="Drafts" value={s?.drafts} accent="text-zinc-400" />
        <Kpi icon={Clock} label="In Review" value={s?.in_review} accent="text-pink-400" />
        <Kpi icon={FileCheck} label="Published" value={s?.published} accent="text-emerald-400" />
      </div>

      <div className="mt-6 bg-[#121212] border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 label-caps text-zinc-500">Recent Activity</div>
        <div className="divide-y divide-zinc-900">
          {(s?.recent_activity || []).length === 0 && (
            <div className="px-4 py-6 text-sm text-zinc-600">No activity yet.</div>
          )}
          {(s?.recent_activity || []).map((a) => (
            <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              <span className="font-mono-data text-xs text-emerald-400 uppercase w-40 truncate">{a.action}</span>
              <span className="text-zinc-400">{a.entity_type}</span>
              <span className="ml-auto text-xs text-zinc-600 font-mono-data">
                {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
